import { Box, Stack, styled } from '@mui/material'

import { ResponsivePie } from '@nivo/pie'
import {
    decimalToLocalizedString,
    EmptyState,
    formatNumberWithAbbreviation,
    isKnownFuelType,
    leftpadWithNonBreakingSpaces,
    Percentage,
    tokens,
} from 'ComponentLibrary'
import { FuelTypeTooltip } from './FuelTypeChartTooltip'

import { sumBy } from 'lodash'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { Typography } from 'ComponentLibrary/src/piazza/text/Typography/Typography'
import { FUEL_TYPE_COLORS_PALETTE } from 'ClientPortal/src/pages/Dashboard/FuelTypeChart/constants'
import { getDynamicColorsArray } from 'ClientPortal/src/pages/Dashboard/FuelTypeChart/colorUtils'
import { KnownFuelTypes } from 'ComponentLibrary/src/common/fuel-type'
import { nivoTooltipWrapperTheme } from 'ComponentLibrary/src/common/nivoTheme'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export type FuelType = {
    fuel_type: KnownFuelTypes | string
    count: number
}

export type FuelTypeChartProps = {
    data: FuelType[]
    title: string
}

const Dot = styled('div')(({ color }: { color: string }) => {
    return {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: color,
    }
})

type LegendLabel = {
    id: string
    color: string
    percentage: string
    label: string
    value: number
}

type LegendLabelsProps = {
    legends: LegendLabel[]
    cutOffThreshold?: number
}

const LegendLabelsFlexContainer = styled('div')(() => ({
    alignContent: 'center',
    paddingRight: tokens.SizingSpacing050,
    display: 'flex',
    justifyContent: 'left',
}))

const LegendLabels = ({ legends, cutOffThreshold }: LegendLabelsProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const maxLegends = cutOffThreshold && cutOffThreshold > 0 ? cutOffThreshold : legends.length
    const legendsToDisplay =
        legends.length > maxLegends
            ? [
                  ...legends.slice(0, maxLegends - 1),
                  {
                      id: 'other-legends',
                      label: tCommon('other'),
                      color: tokens.ColorTextDefault,
                      percentage: new Percentage(sumBy(legends.slice(maxLegends - 1), 'value') / sumBy(legends, 'value'))
                          .format({ locale })
                          .replace(' ', ''),
                      value: sumBy(legends.slice(maxLegends - 1), 'value'),
                  },
              ]
            : legends

    return (
        <Stack direction="column" gap={tokens.SizingSpacing100}>
            {legendsToDisplay.map(({ id, label, color, percentage }) => {
                return (
                    <Stack key={id} direction="row" width="100%" gap={tokens.SizingSpacing050} alignItems="center">
                        <Dot color={color} />
                        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                            <Typography variant="headline5" color={tokens.ColorTextBold}>
                                {leftpadWithNonBreakingSpaces(percentage, 2)}
                            </Typography>
                            <Typography variant="textS" color={tokens.ColorTextBold}>
                                {label}
                            </Typography>
                        </Stack>
                    </Stack>
                )
            })}
        </Stack>
    )
}

const FuelTypeChart = ({ data, title }: FuelTypeChartProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tFuelTypes } = useTranslation('terminology', { keyPrefix: 'common.fuelTypes' })
    const locale = useCurrentResolvedLocale()
    const coloredData = getDynamicColorsArray(data, FUEL_TYPE_COLORS_PALETTE)

    const total = sumBy(coloredData, 'count')

    const legends = coloredData
        .map(({ value, id, label, color }) => {
            const normalizedLabel: string = isKnownFuelType(label) ? tFuelTypes(label) : label
            const percentage = total ? new Percentage(value / total).format({ locale }).replace(' ', '') : '100%'
            // To ensure symbols align always abbreviate numbers
            const valueWithAbbreviation = formatNumberWithAbbreviation(value, 1, true, undefined, locale)
            // Abbreviation with space is of length 4, max value unabbreviated is 9999, euro sign adds two characters therefore pad to 4+4+2=10
            const valueLabel = leftpadWithNonBreakingSpaces(`${valueWithAbbreviation} €`, 10)
            return { id, percentage, label: normalizedLabel, color, value, valueLabel }
        })
        .filter((item) => item.value > 0)

    return (
        <Stack height="90%" width="100%" direction="column" gap={'16px'}>
            <Headline variant="headline2" title={title} />
            {total === 0 && <EmptyState title={tCommon('notEnoughDataAvailableYet')} size="medium" textColor={tokens.ColorTextDefault} />}
            <Stack direction="row" gap={'2px'} alignItems="center">
                <Box height="162px" width="162px" marginX="0px" flexGrow={1}>
                    <ResponsivePie
                        data={coloredData}
                        margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
                        innerRadius={0.75}
                        padAngle={2}
                        cornerRadius={2}
                        arcLinkLabel={'label'}
                        arcLinkLabelsDiagonalLength={10}
                        arcLinkLabelsStraightLength={6}
                        activeOuterRadiusOffset={2}
                        borderWidth={0}
                        colors={{ datum: 'data.color' }}
                        layers={['arcs']}
                        theme={nivoTooltipWrapperTheme}
                        valueFormat={(value) => `${decimalToLocalizedString(value, false, 2, undefined, locale)}`}
                        tooltip={({ datum }) => <FuelTypeTooltip datum={datum} total={total} />}
                    />
                </Box>
                <LegendLabelsFlexContainer>
                    <LegendLabels legends={legends} cutOffThreshold={6} />
                </LegendLabelsFlexContainer>
            </Stack>
        </Stack>
    )
}

export default FuelTypeChart
